import { FunctionComponent } from "react";
import classnames from "classnames";

import { MarketBlurbsProps, MarketBlurbsButtonsProps, AzSwitcherProps } from "./MarketBlurbs.types";
import { GenericIcon } from "@ppb/the-wall-icons/GenericIcon/GenericIcon";
import { MarketIconName, SportsIconName, SupportingContentIconName, OthersIconName } from "@ppb/the-wall-icons";

import { MarketDepthButton, MarketStatus, Label, MarketPromo } from "@ppb/the-wall-web";
import { Switch } from "@ppb/the-wall-web/components/bricks/Switch/Switch";

import styles from "./MarketBlurbs.web.css";

const MarketBlurbsButtons: FunctionComponent<MarketBlurbsButtonsProps> = ({
  marketDepthActive,
  marketInfoCallback,
  marketDepthCallback,
  marketGraphCallback,
  turnInPlayEnabled,
  inplay,
  spBettingActive,
  spBettingCallback,
  spBettingDisabled,
}) => (
  <>
    {marketInfoCallback && (
      <button className={styles.marketBlurbsButton} type="button" aria-label="market info" onClick={marketInfoCallback}>
        <GenericIcon name={MarketIconName.MARKET_RULES} />
      </button>
    )}
    {marketDepthCallback && <MarketDepthButton isActive={marketDepthActive} onClick={marketDepthCallback} />}
    {marketGraphCallback && (
      <button
        className={styles.marketGraphButton}
        type="button"
        aria-label="market graph"
        onClick={marketGraphCallback}
      >
        <GenericIcon name={SupportingContentIconName.MARKET_GRAPH} />
      </button>
    )}
    {spBettingCallback && (
      <button
        className={classnames(styles.marketBlurbsButton, styles.spBettingButton, {
          [styles.spBettingButtonActive]: spBettingActive,
        })}
        type="button"
        aria-label="starting price"
        onClick={spBettingCallback}
        disabled={spBettingDisabled}
      >
        <GenericIcon name={OthersIconName.STARTING_PRICE} />
      </button>
    )}
    {turnInPlayEnabled && (
      <span className={styles.marketBlurbsButton}>
        <GenericIcon
          name={SportsIconName.IN_PLAY}
          color={`var(${inplay ? "--icon-button-in-play-icon-colour" : "--icon-button-default-icon-colour"})`}
        />
      </span>
    )}
  </>
);

const AzSwitcher: FunctionComponent<{ azSwitcherProps: AzSwitcherProps }> = ({ azSwitcherProps }) => {
  const { text, isLeftPosition, isChecked, callback, checkboxId, checkboxName } = azSwitcherProps;

  const azSwitcherContainerClassNames = classnames(styles.azSwitcherContainer, {
    [styles.azSwitcherRightPosition]: !isLeftPosition,
  });

  return (
    <div className={azSwitcherContainerClassNames}>
      <span className={styles.azSwitcherText}>{text}</span>
      <Switch
        label={text}
        isChecked={!!isChecked}
        onChange={callback}
        checkboxId={checkboxId}
        checkboxName={checkboxName}
      />
    </div>
  );
};

export const MarketBlurbs: FunctionComponent<MarketBlurbsProps> = ({
  text,
  columns,
  marketStatus,
  bookPercentage,
  bog,
  marketDepthActive,
  columnGrid,
  azSwitcherProps,
  marketInfoCallback,
  marketDepthCallback,
  marketGraphCallback,
  marketPromo,
  onMarketPromoClick,
  turnInPlayEnabled,
  inplay,
  spBettingActive,
  spBettingCallback,
  spBettingDisabled,
}) => {
  const columnsClassnames = classnames(styles.columns, columnGrid && styles.columnGrid);
  const hasRow =
    azSwitcherProps ||
    marketInfoCallback ||
    marketDepthCallback ||
    marketGraphCallback ||
    text ||
    columns ||
    marketStatus ||
    bog ||
    turnInPlayEnabled ||
    spBettingCallback;
  const hasMarketBlurbsButtons =
    marketInfoCallback || marketDepthCallback || marketGraphCallback || turnInPlayEnabled || spBettingCallback;

  return (
    <div className={styles.marketBlurbs}>
      {marketPromo && <MarketPromo {...marketPromo} onMarketPromoClick={onMarketPromoClick} />}
      {!!hasRow && (
        <div className={styles.row}>
          {azSwitcherProps?.isLeftPosition ? <AzSwitcher azSwitcherProps={azSwitcherProps} /> : null}
          {hasMarketBlurbsButtons && (
            <MarketBlurbsButtons
              marketDepthActive={marketDepthActive}
              marketInfoCallback={marketInfoCallback}
              marketDepthCallback={marketDepthCallback}
              marketGraphCallback={marketGraphCallback}
              turnInPlayEnabled={turnInPlayEnabled}
              inplay={inplay}
              spBettingActive={spBettingActive}
              spBettingCallback={spBettingCallback}
              spBettingDisabled={spBettingDisabled}
            />
          )}
          {!!text && <span className={styles.text}>{text}</span>}
          {columns && !marketStatus && !bog && (
            <div className={columnsClassnames}>
              {columns.map((col, index) => (
                <span key={index} className={styles.column}>
                  {col}
                </span>
              ))}
            </div>
          )}
          {marketStatus && (
            <div className={styles.marketStatus}>
              <MarketStatus status={marketStatus} />
            </div>
          )}
          {!marketStatus && bog && (
            <div className={styles.bogContainer}>
              <Label text={bog} />
            </div>
          )}
          {azSwitcherProps && !azSwitcherProps.isLeftPosition ? <AzSwitcher azSwitcherProps={azSwitcherProps} /> : null}
        </div>
      )}

      {bookPercentage && (
        <div className={styles.bookPercentage}>
          <div className={styles.back}>
            <span>{bookPercentage.back.percentage}</span>
            <span>{bookPercentage.back.label}</span>
          </div>
          <div className={styles.lay}>
            <span>{bookPercentage.lay.label}</span>
            <span>{bookPercentage.lay.percentage}</span>
          </div>
        </div>
      )}
    </div>
  );
};
